import type { ApiResponse } from "#layers/cms/shared/types/ApiResponse"
import type { BucketData } from "#layers/cms/shared/types/bucketData"
import type { BucketMeta, MetaProperties } from "#layers/cms/shared/types/bucketMeta"

type ContentReferenceOptions = {
    sourceBucket: string
    sourceData: Record<string, unknown> | Promise<Record<string, unknown>>
    fieldPath: string
}

const get_value_at_path = (source: unknown, path: string): unknown => {
    return path.split('.').reduce<unknown>((value, key) => {
        if (typeof value !== 'object' || value === null) return undefined
        return (value as Record<string, unknown>)[key]
    }, source)
}

const get_property_at_path = (
    properties: Record<string, MetaProperties>,
    path: string
): MetaProperties | undefined => {
    const keys = path.split('.')
    let property: MetaProperties | undefined
    let current_properties = properties

    for (const key of keys) {
        property = current_properties[key]
        if (!property) return undefined
        current_properties = property.properties ?? {}
    }

    return property
}

export const useContentReference = async ({
    sourceBucket,
    sourceData,
    fieldPath
}: ContentReferenceOptions) => {
    const requestFetch = useRequestFetch()
    const { get_value: get_localized_value } = useLocalizedValue()
    const content = ref<BucketData | null>(null)
    const property = ref<MetaProperties | null>(null)

    // Bucket schema only depends on sourceBucket, so fetch it in parallel with sourceData
    // resolving instead of waiting for sourceData first and only then starting this request.
    const meta_promise = requestFetch<ApiResponse<BucketMeta>>(`/api/buckets/${sourceBucket}`)
    const resolved_source_data = await sourceData
    const content_id = get_value_at_path(resolved_source_data, fieldPath)

    if (typeof content_id === 'string' && content_id) {
        const meta_result = await meta_promise
        if (!meta_result.success) throw new Error(meta_result.message)

        const reference_property = get_property_at_path(
            meta_result.data.jsonSchema.properties,
            fieldPath
        )

        if (reference_property?.renderer === 'refSelect' && reference_property._ref) {
            const content_result = await requestFetch<ApiResponse<BucketData> | null>(
                `/api/bucketContent/${reference_property._ref}/${content_id}`
            )
            if (content_result && !content_result.success) {
                throw new Error(content_result.message)
            }

            property.value = reference_property
            content.value = content_result?.data ?? null
        }
    } else {
        // No reference to resolve, but the request is already in flight — await it so a
        // failure doesn't surface as an unhandled rejection.
        await meta_promise.catch(() => undefined)
    }

    const label = computed(() => {
        if (!content.value || !property.value) return ''
        const field = property.value.rendererOptions?.labelField || 'title'
        const value = get_localized_value(content.value.data[field])
        return value == null ? '' : String(value)
    })

    const image = computed(() => {
        if (!content.value || !property.value) return undefined
        const options = property.value.rendererOptions
        const field = options === undefined ? 'hero-image' : options.imageField
        if (!field) return undefined

        const value = get_localized_value(content.value.data[field])
        return value == null ? undefined : String(value)
    })

    return {
        content,
        property,
        label,
        image
    }
}

export const useContentReferences = async ({
    sourceBucket,
    sourceData,
    fieldPath
}: ContentReferenceOptions) => {
    const requestFetch = useRequestFetch()
    const content = ref<Record<string, unknown>[]>([])
    const property = ref<MetaProperties | null>(null)
    const content_ids = get_value_at_path(sourceData, fieldPath)


    console.log(1)
    if (!Array.isArray(content_ids) || content_ids.length === 0) {
        return { content, property }
    }

    const meta_result = await requestFetch<ApiResponse<BucketMeta>>(
        `/api/buckets/${sourceBucket}`
    )
    if (!meta_result.success) throw new Error(meta_result.message)

    const collection_property = get_property_at_path(
        meta_result.data.jsonSchema.properties,
        fieldPath
    )
    const item_property = collection_property?.items
    const nested_reference = Object.entries(item_property?.properties ?? {}).find(
        ([, nested_property]) => nested_property.renderer === 'refSelect'
    )
    const reference_key = nested_reference?.[0]
    const reference_property = item_property?.renderer === 'refSelect'
        ? item_property
        : nested_reference?.[1]

    if (reference_property?.renderer !== 'refSelect' || !reference_property._ref) {
        return { content, property }
    }
    const reference_bucket = reference_property._ref
    const reference_ids = content_ids.flatMap(item => {
        if (typeof item === 'string') return [item]
        if (!reference_key || typeof item !== 'object' || item === null) return []

        const reference_id = (item as Record<string, unknown>)[reference_key]
        return typeof reference_id === 'string' && reference_id ? [reference_id] : []
    })

    const content_results = await Promise.all(
        reference_ids
            .map(reference_id => requestFetch<ApiResponse<BucketData> | null>(
                `/api/bucketContent/${reference_bucket}/${reference_id}`
            ))
    )

    const failed_result = content_results.find(
        result => result !== null && !result.success
    )
    if (failed_result && !failed_result.success) {
        throw new Error(failed_result.message)
    }

    property.value = reference_property
    content.value = content_results.flatMap(result => {
        return result?.success ? [result.data.data] : []
    })

    return { content, property }
}
